import {
  isRouteErrorResponse,
  Link,
  useNavigate,
  useRouteError,
} from "react-router-dom";
import { ROOT, LOGIN } from "@/lib/links";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { ArrowCounterClockwise, House } from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
function ErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  Sentry.captureException(error, {
    tags: {
      route: location.pathname,
    },
  });
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <div className="flex items-center justify-center min-h-screen w-dvw bg-background">
        <Card className="w-[420px]">
          <CardHeader>
            <CardTitle className="text-4xl font-bold text-center">
              404
            </CardTitle>
            <CardDescription className="text-xl text-center">
              Page Not Found
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">
              Oops! The page you're looking for doesn't exist or has been moved.
            </p>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button asChild>
              <Link to={ROOT}>Go Back Home</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (
    isRouteErrorResponse(error) &&
    (error.status === 403 || error.status === 401)
  ) {
    navigate(LOGIN, { replace: true });
    return null; // Redirecting to login, no need to render anything else
  }

  return (
    <div className="flex items-center justify-center min-h-screen w-dvw bg-background">
      <Card className="mx-10">
        <CardHeader>
          <CardTitle className="text-4xl font-bold text-center">
            Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-center text-destructive">
            {isRouteErrorResponse(error)
              ? error.statusText
              : "An unexpected error occurred."}
          </CardDescription>
          <p>
            If you encounter this error, please email
            <a
              href="mailto:support@arcology.io"
              className="underline text-primary"
            >
              support@arcology.io
            </a>
            . Include the error details below to help us resolve the issue:
          </p>
          <pre className="h-40 p-4 mt-2 overflow-auto rounded-md whitespace-break-spaces bg-muted">
            {error instanceof Error
              ? error.message + "\n" + error.stack
              : JSON.stringify(error, null, 2)}
          </pre>
        </CardContent>
        <CardFooter className="flex flex-col justify-center gap-4">
          <Button onClick={() => window.location.reload()} className="w-full">
            <ArrowCounterClockwise className="w-4 h-4 mr-2" />
            Refresh Page
          </Button>
          <Button asChild className="w-full">
            <Link to={ROOT}>
              <House className="w-4 h-4 mr-2" />
              Go Back Home
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

export default ErrorBoundary;
